import FlexRow from '@components/display/FlexRow'
import FlexColumn from '@components/display/FlexColumn'
import FlexItem from '@components/display/FlexItem'

import ProgressContextProvider from '@context/ProgressContext'
import Form from '@components/form/FormWithContext'

import Label from '@components/form/context/LabelH'
import HiddenInput from '@components/form/context/HiddenInput'
import RadioInput from '@components/form/context/RadioInput'
import Checkboxes from '@components/form/context/Checkboxes'
import Input from '@components/form/context/Input'
import FuelInput from '@components/form/context/FuelInput'
import SpecSummary from '@components/add/SpecSummary'
import DateInput from '@components/form/context/DateInput'
import LangInput from '@components/form/context/LangInput'
import Checkbox from '@components/form/context/Checkbox'
import Textarea from '@components/form/context/Textarea'
import Equipment from '@components/add/Equipment'
import PhotosInput from '@components/form/photos/DefaultPhotosInput'
import LocationSelect from '@components/add/LocationSelect'
import LeasingRow from '@components/add/LeasingRow'
import SubmitButton from '@components/form/context/SubmitButton'
import Icon from '@components/shared/Icon'

import useT from '@hooks/useTranslation'

import stan from '@components/add/forms/inputs/stan'
import stanDodatkowy from '@components/add/forms/inputs/stanDodatkowy'
import marka from '@components/add/forms/inputs/markaTruck'
import model from '@components/add/forms/inputs/modelTruck'
import typ from '@components/search/inputs/typTruck'
import seria from '@components/add/forms/inputs/seria'
import silnik from '@components/add/forms/inputs/silnik'
import paliwo from '@components/search/inputs/paliwo'

import rokProdukcji from '@components/add/forms/inputs/rokProdukcji'
import rokModelowy from '@components/add/forms/inputs/rokModelowy'
import dataPierwszejRejestracji from '@components/add/forms/inputs/dataPierwszejRejestracji'
import przebieg from '@components/add/forms/inputs/przebieg'
import vin from '@components/add/forms/inputs/vin'
import jezyk from '@components/add/forms/inputs/jezyk'
import krajPochodzenia from '@components/search/inputsAdvanced/krajPochodzenia'

import dopuszczalnaLadownosc from '@components/add/forms/inputs/dopuszczalnaLadownosc'
import dopuszczalnaMasaCalkowita from '@components/add/forms/inputs/dopuszczalnaMasaCalkowita'
import kubatura from '@components/add/forms/inputs/kubatura'
import krajRejestracji from '@components/add/forms/inputs/krajRejestracji'
import numerRejestracyjny from '@components/add/forms/inputs/numerRejestracyjny'
import emisjaCO2 from '@components/add/forms/inputs/emisjaCO2'

import pojemnoscSkokowa from '@components/add/forms/inputs/pojemnoscSkokowa'
import moc from '@components/add/forms/inputs/moc'
import skrzyniaBiegow from '@components/add/forms/inputs/skrzyniaBiegow'
import naped from '@components/add/forms/inputs/naped'
import liczbaMiejsc from '@components/add/forms/inputs/liczbaMiejsc'
import kolor from '@components/add/forms/inputs/kolor'

import podwojneTylneKola from '@components/add/forms/inputs/podwojneTylneKola'
import agregatChlodzacy from '@components/add/forms/inputs/agregatChlodzacy'
import adblue from '@components/add/forms/inputs/adblue'
import filtrCzastekStalych from '@components/add/forms/inputs/filtrCzastekStalych'
import kierownicaPoPrawej from '@components/add/forms/inputs/kierownicaPoPrawej'

import opis from '@components/add/forms/inputs/opis'
import wideo from '@components/add/forms/inputs/wideo'
import cena from '@components/add/forms/inputs/cena'
import rata from '@components/add/forms/inputs/rata'
import waluta from '@components/add/forms/inputs/waluta'
import cenaDodatkowe from '@components/add/forms/inputs/cenaDodatkowe'
import { carSafety, carComfort } from '@constants/values/equipment'
import { v1 as uuid } from 'uuid'

import HotUserRegistrationInput from '@components/add/HotUserRegistrationInput'
import Maybe from '@components/shared/Maybe'
import isTempUser from '@utils/user/isTempUser'

const TruckForm = ({ user, offer, ...rest }) =>
    <ProgressContextProvider>
        <Form {...rest} progressbar>
            { offer?._id && <HiddenInput name="_id" defaultValue={offer._id} /> }
            <HiddenInput name="pid" defaultValue={offer?.pid || uuid()} />
            <FlexRow>
                <FlexItem shrink>
                    <Label text="Stan pojazdu" required />
                    <RadioInput {...stan} required error />
                </FlexItem>

                <FlexItem>
                    <Label text="Dodatkowy stan pojazdu" />
                    <Checkboxes {...stanDodatkowy} />
                </FlexItem>
            </FlexRow>

            <FlexRow>
                <FlexItem>
                    <Label text="Specyfikacja samochodu dostawczego" />
                    <Input {...marka} required error />
                    <Input {...model} required error />
                    <Input {...typ} selectlike />
                    <Input {...seria} />
                    <Input {...silnik} />
                    <FuelInput options={paliwo} defaultValue={offer?.paliwo}
                        singlevalue required error />
                </FlexItem>

                <FlexItem half nopadding>
                    <SpecSummary />
                </FlexItem>
            </FlexRow>

            <FlexRow>
                <FlexItem>
                    <Label text="Specyfikacja użytkowania" />
                    <Input {...rokProdukcji} required error />
                    <Input {...rokModelowy} error />
                    <DateInput {...dataPierwszejRejestracji} />
                    <Input {...przebieg} required error />
                    <Input {...vin} error />
                </FlexItem>

                <FlexItem half nopadding>
                    <FlexColumn>
                        <FlexItem>
                            <Label text="Język ogłoszenia" />
                            <LangInput {...jezyk} />
                        </FlexItem>

                        <FlexItem>
                            <Label text="Kraj pochodzenia" />
                            <Input {...krajPochodzenia} selectlike />
                        </FlexItem>
                    </FlexColumn>
                </FlexItem>
            </FlexRow>

            <FlexRow>
                <FlexItem>
                    <Label text="Szczegółowe parametry" />
                    <Input {...dopuszczalnaLadownosc} />
                    <Input {...dopuszczalnaMasaCalkowita} />
                    <Input {...kubatura} />
                    <Input {...krajRejestracji} selectlike />
                    <Input {...numerRejestracyjny} error />
                    <Input {...emisjaCO2} />
                </FlexItem>

                <FlexItem>
                    <br /><br />
                    <Input {...pojemnoscSkokowa} />
                    <Input {...moc} />
                    <Input {...skrzyniaBiegow} selectlike />
                    <Input {...naped} selectlike />
                    <Input {...liczbaMiejsc} />
                    <Input {...kolor} selectlike />
                </FlexItem>

                <FlexItem>
                    <br /><br />
                    <Checkbox {...podwojneTylneKola} /><br /><br />
                    <Checkbox {...agregatChlodzacy} /><br /><br />
                    <Checkbox {...adblue} /><br /><br />
                    <Checkbox {...filtrCzastekStalych} /><br /><br />
                    <Checkbox {...kierownicaPoPrawej} />
                </FlexItem>
            </FlexRow>

            <FlexRow>
                <FlexItem>
                    <Label text="Wyposażenie" />
                    <Equipment safety={carSafety} comfort={carComfort}
                        defaultValue={offer?.wyposazenie} />
                </FlexItem>

                <FlexItem half>
                    <Label text="Dodatkowy opis" />
                    <Textarea {...opis} error />
                </FlexItem>
            </FlexRow>

            <FlexRow>
                <FlexItem>
                    <Label text="Zdjęcia" required />
                    <PhotosInput user={user} defaultValue={rest.defaultValues?.pid} required error />
                    <Label text="Materiał wideo" />
                    <Input {...wideo} error />
                </FlexItem>
            </FlexRow>

            <FlexRow>
                <FlexItem>
                    <Label text="Lokalizacja" required />
                    <LocationSelect user={user} defaultValue={offer?.lokalizacja} required />
                </FlexItem>
            </FlexRow>

            <FlexRow>
                <FlexColumn>
                    <FlexRow inner>
                        <FlexItem>
                            <Label text="Cena" />
                            <Input {...cena} required error />
                            <Input {...rata} error />
                        </FlexItem>
                        <FlexItem>
                            <br /><br />
                            <Input {...waluta} defaultValue="PLN" required selectlike />
                        </FlexItem>
                    </FlexRow>

                    <FlexItem>
                        <Checkboxes {...cenaDodatkowe} />
                    </FlexItem>
                </FlexColumn>
            </FlexRow>

            <LeasingRow />

            <Maybe test={isTempUser(user)}>
                <FlexRow>
                    <HotUserRegistrationInput />
                </FlexRow>
            </Maybe>

            <SubmitButton>{useT('Zapisz ogłoszenie')} <Icon>arrow_right_alt</Icon></SubmitButton>
        </Form>
    </ProgressContextProvider>

export default TruckForm